import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  shareReplay,
  Subject,
  tap,
} from 'rxjs';
import { FLAGS, FlagsService } from 'src/app/services/flags.service';
import { LocalStorageItemIDs, LocalStorageService } from 'src/app/services/local-storage.service';
import { ProviderStateService } from 'src/app/services/provider-state.service';
import {
  IExpandAndFullScreenChange,
  ISessionViewState,
  SessionSharedDataService,
  SessionView,
} from 'src/app/services/session-shared-data.service';
import { UiService } from 'src/app/services/ui.service';
import { TrackType } from 'src/app/common/interfaces/rtc-interface';
import { UserService } from 'src/app/services/user.service';
import { ObserverModeService } from 'src/app/services/observer-mode.service';
import { PictureInPictureService } from './picture-in-picture.service';

export enum OneOnOneModePosition {
  BOTTOM_RIGHT = 'bottom-right',
  TOP_RIGHT = 'top-right',
  BOTTOM_LEFT = 'bottom-left',
  TOP_LEFT = 'top-left',
}

export interface OneOnOneUiViewModel {
  readonly overlaySizePercent: number;
  readonly overlayAspectRatio: number;
  readonly hideNameTag: boolean;
  readonly hideInstitutionLogo: boolean;
  readonly hideInactiveIndicator: boolean;
  readonly hideReactions: boolean;
  readonly hideRaiseHandIcon: boolean;
  readonly hideSpeakingIcon: boolean;
  readonly hideErrorIndicator: boolean;
  readonly profileImageSize: number;
  readonly selfTileResizeEnabled: boolean;
}

const VCT_OVERLAY_SIZE_PERCENT = 0.08;
const VCT_OVERLAY_ASPECT_RATIO = 1;
const GV_OVERLAY_SIZE_PERCENT = 0.025;
const GV_OVERLAY_ASPECT_RATIO = 112 / 202;
const PROFILE_IMAGE_SIZE = 32;
const MOBILE_GV_OVERLAY_SIZE_PERCENT = 0.056;
const MOBILE_GV_OVERLAY_ASPECT_RATIO = 150 / 90;

// GV overlay resize bounds (area-based percent of the large tile).
// These are further clamped by the available width/height when calculating a concrete pixel size.
const GV_OVERLAY_SIZE_PERCENT_MIN = 0.025;
const GV_OVERLAY_SIZE_PERCENT_MAX = 0.07;

@Injectable()
@UntilDestroy()
export class OneOnOneModeService {
  private _isOneOnOneModeDisabledLocally = false;

  private readonly tileCount$ = new BehaviorSubject<number>(0);

  private readonly _shouldOneOnOneBeActive$ = new BehaviorSubject<boolean>(false);
  public readonly shouldOneOnOneBeActive$ = this._shouldOneOnOneBeActive$
    .asObservable()
    .pipe(distinctUntilChanged(), shareReplay(1));

  private readonly _isOneOnOneModeActive$ = new BehaviorSubject<boolean>(false);
  public readonly isOneOnOneModeActive$ = this._isOneOnOneModeActive$
    .asObservable()
    .pipe(distinctUntilChanged(), shareReplay(1));

  private readonly _overlayPosition$ = new BehaviorSubject<OneOnOneModePosition>(
    OneOnOneModePosition.BOTTOM_RIGHT,
  );
  public readonly overlayPosition$ = this._overlayPosition$
    .asObservable()
    .pipe(distinctUntilChanged(), shareReplay(1));

  private readonly _isOverlayDragging$ = new BehaviorSubject<boolean>(false);
  public readonly isOverlayDragging$ = this._isOverlayDragging$
    .asObservable()
    .pipe(distinctUntilChanged(), shareReplay(1));

  private readonly _gvOverlaySizePercent$ = new BehaviorSubject<number>(GV_OVERLAY_SIZE_PERCENT);
  public readonly gvOverlaySizePercent$ = this._gvOverlaySizePercent$
    .asObservable()
    .pipe(distinctUntilChanged(), shareReplay(1));

  private readonly _forceUpdateTiles = new Subject<void>();
  public readonly forceUpdateTiles$ = this._forceUpdateTiles.asObservable();

  readonly oneOnOneUiVm$ = combineLatest([
    this.sharedDataService.sessionView.current$.pipe(
      map((sessionView: ISessionViewState) => sessionView.view),
      distinctUntilChanged(),
    ),
    this.gvOverlaySizePercent$,
    this.uiService.isMobile.pipe(distinctUntilChanged()),
    this.pictureInPictureService.isPIPActive$.pipe(distinctUntilChanged()),
  ]).pipe(
    map(([sessionView, gvOverlaySizePercent, isMobile, isPIPActive]): OneOnOneUiViewModel => {
      const isDesktopGalleryViewCase =
        (sessionView === SessionView.GALLERY_VIEW ||
          sessionView === SessionView.FULLSCREEN ||
          isPIPActive) &&
        !isMobile;
      const isMobileGalleryViewCase = sessionView === SessionView.GALLERY_VIEW && isMobile;

      if (isMobileGalleryViewCase) {
        return {
          overlaySizePercent: MOBILE_GV_OVERLAY_SIZE_PERCENT,
          overlayAspectRatio: MOBILE_GV_OVERLAY_ASPECT_RATIO,
          hideNameTag: true,
          hideInstitutionLogo: true,
          hideInactiveIndicator: true,
          hideReactions: true,
          hideRaiseHandIcon: true,
          hideSpeakingIcon: true,
          hideErrorIndicator: true,
          profileImageSize: PROFILE_IMAGE_SIZE,
          selfTileResizeEnabled: false,
        };
      }

      if (isDesktopGalleryViewCase) {
        return {
          overlaySizePercent: gvOverlaySizePercent,
          overlayAspectRatio: GV_OVERLAY_ASPECT_RATIO,
          hideNameTag: true,
          hideInstitutionLogo: true,
          hideInactiveIndicator: true,
          hideReactions: true,
          hideRaiseHandIcon: true,
          hideSpeakingIcon: true,
          hideErrorIndicator: true,
          profileImageSize: PROFILE_IMAGE_SIZE,
          selfTileResizeEnabled: true,
        };
      }

      return {
        overlaySizePercent: VCT_OVERLAY_SIZE_PERCENT,
        overlayAspectRatio: VCT_OVERLAY_ASPECT_RATIO,
        hideNameTag: true,
        hideInstitutionLogo: true,
        hideInactiveIndicator: true,
        hideReactions: true,
        hideRaiseHandIcon: true,
        hideSpeakingIcon: true,
        hideErrorIndicator: true,
        profileImageSize: PROFILE_IMAGE_SIZE,
        selfTileResizeEnabled: false,
      };
    }),
    distinctUntilChanged(
      (a, b) =>
        a.overlaySizePercent === b.overlaySizePercent &&
        a.overlayAspectRatio === b.overlayAspectRatio &&
        a.hideNameTag === b.hideNameTag &&
        a.hideInstitutionLogo === b.hideInstitutionLogo &&
        a.hideInactiveIndicator === b.hideInactiveIndicator &&
        a.hideRaiseHandIcon === b.hideRaiseHandIcon &&
        a.hideSpeakingIcon === b.hideSpeakingIcon &&
        a.hideErrorIndicator === b.hideErrorIndicator &&
        a.profileImageSize === b.profileImageSize,
    ),
    shareReplay(1),
  );

  constructor(
    private readonly providerState: ProviderStateService,
    private readonly flagsService: FlagsService,
    private readonly localStorageService: LocalStorageService,
    private readonly sharedDataService: SessionSharedDataService,
    private readonly uiService: UiService,
    private readonly userService: UserService,
    private readonly pictureInPictureService: PictureInPictureService,
    private readonly observerModeService: ObserverModeService,
  ) {
    this._isOneOnOneModeDisabledLocally = !this.isOneOnOneEnabledInLocalStorage();

    combineLatest([
      this.providerState.callConnected$.pipe(distinctUntilChanged()),
      this.flagsService
        .featureFlagChanged(FLAGS.ENABLE_ONE_ON_ONE_VIDEO_CALL_UI)
        .pipe(distinctUntilChanged()),
      this.tileCount$.pipe(distinctUntilChanged()),
      this.sharedDataService.sessionView.current$,
      this.uiService.isMobile.pipe(distinctUntilChanged()),
    ])
      .pipe(
        untilDestroyed(this),
        map(([callConnected, enabled, tileCount, currentSessionViewState, isMobile]) => {
          // we never allow 1:1 mode in observer mode since no self tile at all
          if (this.observerModeService.isObserverModeActive()) {
            return false;
          }
          // we allow  1:1 mode on mobile only in GV for now
          // we allow  1:1 mode on Desktop only in GV and VCT and Fullscreen
          const currentSessionView = currentSessionViewState.view;
          const isOneOnOneModeActive =
            enabled &&
            (this.isFullScreenAnotherTile(
              currentSessionView,
              isMobile,
              currentSessionViewState.expandedFullScreenChange,
            ) ||
              this.isInOneOnOneState(currentSessionView, isMobile, tileCount, callConnected));

          return isOneOnOneModeActive;
        }),
        tap((shouldOneOnOneBeActive) => {
          this._shouldOneOnOneBeActive$.next(shouldOneOnOneBeActive);
          this._isOneOnOneModeActive$.next(
            shouldOneOnOneBeActive && !this._isOneOnOneModeDisabledLocally,
          );
        }),
      )
      .subscribe();
  }

  public isFullScreenAnotherTile(
    currentSessionView: SessionView,
    isMobile: boolean,
    expandAndFullScreenChange: IExpandAndFullScreenChange | null | undefined,
  ) {
    return (
      currentSessionView === SessionView.FULLSCREEN &&
      !isMobile &&
      expandAndFullScreenChange &&
      (expandAndFullScreenChange.tileData.trackType !== TrackType.VIDEO ||
        expandAndFullScreenChange.tileData.userId !== this.sharedDataService.user?._id)
    );
  }

  private isInOneOnOneState(
    currentSessionView: SessionView,
    isMobile: boolean,
    tileCount: number,
    callConnected: boolean,
  ) {
    const isFullscreen = currentSessionView === SessionView.FULLSCREEN;
    const isExpanded = currentSessionView === SessionView.EXPANDED;
    const isGalleryView = currentSessionView === SessionView.GALLERY_VIEW;

    return (
      !isFullscreen &&
      !isExpanded &&
      callConnected &&
      tileCount === 2 &&
      (!isMobile || isGalleryView)
    );
  }

  updateTileCount(count: number): void {
    this.tileCount$.next(count);
  }

  updateOverlayPosition(position: OneOnOneModePosition): void {
    this._overlayPosition$.next(position);
  }

  setOverlayDragging(isDragging: boolean): void {
    this._isOverlayDragging$.next(isDragging);
  }

  updateGvOverlaySizePercent(sizePercent: number): void {
    const clamped = Math.min(
      GV_OVERLAY_SIZE_PERCENT_MAX,
      Math.max(GV_OVERLAY_SIZE_PERCENT_MIN, sizePercent),
    );
    this._gvOverlaySizePercent$.next(clamped);
  }

  disableOneOnOneModeInLocalStorage(): void {
    this.localStorageService.setItem(
      LocalStorageItemIDs.DISABLE_ONE_ON_ONE,
      'true',
      '',
      this.userService.userId ?? '',
    );
    this._isOneOnOneModeDisabledLocally = true;
    this._isOneOnOneModeActive$.next(false);
    this._forceUpdateTiles.next();
  }

  enableOneOnOneModeInLocalStorage(): void {
    this.localStorageService.setItem(
      LocalStorageItemIDs.DISABLE_ONE_ON_ONE,
      'false',
      '',
      this.userService.userId ?? '',
    );
    this._isOneOnOneModeDisabledLocally = false;
    this._isOneOnOneModeActive$.next(this._shouldOneOnOneBeActive$.getValue());
    this._forceUpdateTiles.next();
  }

  get gvOverlaySizePercentMin(): number {
    return GV_OVERLAY_SIZE_PERCENT_MIN;
  }

  get gvOverlaySizePercentMax(): number {
    return GV_OVERLAY_SIZE_PERCENT_MAX;
  }

  get overlayPosition(): OneOnOneModePosition {
    return this._overlayPosition$.getValue();
  }

  get isOverlayDragging(): boolean {
    return this._isOverlayDragging$.getValue();
  }

  get gvOverlaySizePercent(): number {
    return this._gvOverlaySizePercent$.getValue();
  }

  get isOneOnOneModeActive(): boolean {
    return this._isOneOnOneModeActive$.getValue();
  }

  // covers both cases when the field is set to true or false or not set at all
  private isOneOnOneEnabledInLocalStorage(): boolean {
    return !(
      this.localStorageService.getItem(
        LocalStorageItemIDs.DISABLE_ONE_ON_ONE,
        '',
        this.userService.userId ?? '',
      ) === 'true'
    );
  }
}
